import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SupportLayout from './SupportLayout'
import { useAuth } from '../../hooks/useAuth'
import { usePractice } from '../../hooks/usePractice'
import { useToast } from '../../components/Toast'
import { supabase, getSettings } from '../../lib/supabase'
import usePageTitle from '../../hooks/usePageTitle'

// ─── SupportSuggest ──────────────────────────────────────────────────────────
//
// "Submit a suggestion" form. Reuses the contact_form Edge Function with
// source='suggestion', so submissions arrive in the support inbox with a
// [Suggestion] subject prefix that can be filtered.
//
// Deliberately lightweight: one big text area, no category dropdown, no
// priority field. Asking users to triage their own ideas is friction that
// drives down submission rates without helping triage on the receiving end.
//
// Expectation-setting is the most important thing on this page. The copy
// says clearly: we read every suggestion, we can't reply to most, but they
// shape the roadmap. This avoids the "I submitted three things and got
// no reply" frustration that kills feedback culture.

export default function SupportSuggest() {
  const { user }                        = useAuth()
  const { practiceId }                  = usePractice() || {}
  const toast                           = useToast()
  usePageTitle('Suggest a feature')

  const [practiceName, setPracticeName] = useState('')
  const [suggestion, setSuggestion]     = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent]             = useState(false)
  const [error, setError]           = useState('')

  // Fetch the practice name from bookkeeper_settings for the "Sending as"
  // line and the email metadata. Best-effort: if it fails we just skip
  // showing the practice name.
  useEffect(() => {
    if (!practiceId) return
    getSettings(practiceId).then(({ data }) => {
      if (data?.practice_name) setPracticeName(data.practice_name)
    }).catch(() => { /* non-fatal */ })
  }, [practiceId])

  useEffect(() => { window.scrollTo(0, 0) }, [])

  function validate() {
    if (!suggestion.trim()) return 'Please write your suggestion before submitting.'
    if (suggestion.trim().length < 20) {
      return 'Please flesh out the suggestion a bit. A short sentence is hard for us to act on.'
    }
    return null
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setSubmitting(true)
    try {
      const contextLines = [
        practiceName ? `Practice: ${practiceName}` : null,
        `User ID: ${user?.id || 'unknown'}`,
      ].filter(Boolean).join('\n')

      const composedMessage = `${contextLines}\n\n---\n\n${suggestion.trim()}`

      const { error: invokeError } = await supabase.functions.invoke('send-email', {
        body: {
          type:    'contact_form',
          source:  'suggestion',
          name:    (practiceName || user?.email || 'A customer').slice(0, 100),
          email:   (user?.email || '').slice(0, 200),
          firm:    (practiceName || '').slice(0, 150),
          message: composedMessage.slice(0, 6000),
        },
      })
      if (invokeError) throw invokeError

      setSent(true)
      toast("Thanks, suggestion received.", 'success')
    } catch (err) {
      console.error('Support suggest error:', err)
      setError("Something went wrong sending your suggestion. Please try again, or email us directly at hello@receiptchaser.com.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SupportLayout
      back={{ label: 'All support articles', to: '/support' }}
      title="Suggest a feature"
      subtitle="Tell us what would make Receipt Chaser better for the way you actually work."
    >
      <div style={{
        background: 'var(--white)',
        border: '1px solid var(--surface-2)',
        borderRadius: 12,
        padding: 28,
        maxWidth: 720,
      }}>
        {sent ? (
          <SentState onSendAnother={() => {
            setSent(false)
            setSuggestion('')
          }} />
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {/* Honest expectation-setting up front */}
            <div style={{
              background: 'var(--surface)',
              border: '1px solid var(--surface-2)',
              borderRadius: 8,
              padding: '14px 16px',
              marginBottom: 20,
              fontSize: '0.9rem',
              color: 'var(--ink)',
              lineHeight: 1.6,
            }}>
              <strong>A note on what happens next.</strong> We read every
              suggestion that comes in, but we usually can't reply to each
              one individually. Suggestions shape what we build next, so
              the more specific you are about the problem you're trying to
              solve, the more likely your idea ends up in the product. If
              you need an actual answer about something, use{' '}
              <Link to="/support/contact" style={{ color: 'var(--accent)', fontWeight: 500 }}>
                Contact support
              </Link>
              {' '}instead.
            </div>

            {/* Signed-in-as line */}
            <div style={{
              fontSize: '0.82rem',
              color: 'var(--ink-muted)',
              marginBottom: 16,
              lineHeight: 1.55,
            }}>
              Sending as <strong style={{ color: 'var(--ink)' }}>{user?.email}</strong>
              {practiceName && (
                <> at <strong style={{ color: 'var(--ink)' }}>{practiceName}</strong></>
              )}
              .
            </div>

            <FormField label="Your suggestion" htmlFor="suggestion">
              <textarea
                id="suggestion"
                value={suggestion}
                onChange={e => setSuggestion(e.target.value)}
                maxLength={5000}
                rows={10}
                placeholder={SUGGESTION_PLACEHOLDER}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 200, fontFamily: 'inherit' }}
                disabled={submitting}
                required
              />
            </FormField>

            {error && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
                padding: '10px 14px',
                borderRadius: 8,
                fontSize: '0.88rem',
                marginBottom: 16,
                lineHeight: 1.5,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              style={{
                padding: '12px 28px',
                fontSize: '0.95rem',
                width: '100%',
                opacity: submitting ? 0.7 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Sending…' : 'Send suggestion →'}
            </button>
          </form>
        )}
      </div>

      {/* What makes a good suggestion */}
      <div style={{
        marginTop: 32,
        padding: 22,
        background: 'var(--white)',
        border: '1px dashed var(--surface-3)',
        borderRadius: 10,
        maxWidth: 720,
      }}>
        <h3 style={{
          margin: '0 0 12px',
          fontSize: '0.95rem',
          fontWeight: 600,
        }}>
          What helps us most
        </h3>
        <ul style={{
          margin: 0, paddingLeft: 18,
          fontSize: '0.88rem', color: 'var(--ink)',
          lineHeight: 1.7,
        }}>
          <li>
            <strong>The problem you're trying to solve.</strong> "I lose
            track of which clients haven't responded" is more useful than
            "Please add a notifications panel".
          </li>
          <li>
            <strong>How often you hit it.</strong> Once a quarter? Every
            time you send a request? Daily?
          </li>
          <li>
            <strong>What you do today instead.</strong> Tells us how high
            the cost of the missing feature actually is.
          </li>
        </ul>
      </div>
    </SupportLayout>
  )
}

const SUGGESTION_PLACEHOLDER = `What's the suggestion? What problem does it solve? Feel free to be informal. We'd rather have your raw thought than a polished spec.`

// ─── Bits ────────────────────────────────────────────────────────────────────

function FormField({ label, htmlFor, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label
        htmlFor={htmlFor}
        style={{
          display: 'block',
          fontSize: '0.85rem',
          fontWeight: 500,
          color: 'var(--ink)',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  )
}

function SentState({ onSendAnother }) {
  return (
    <div style={{ textAlign: 'center', padding: '24px 8px' }}>
      <div style={{
        width: 52, height: 52,
        borderRadius: '50%',
        background: 'var(--accent-light)',
        color: 'var(--accent)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.6rem',
        marginBottom: 18,
      }}>
        ✓
      </div>
      <h2 style={{
        fontSize: '1.3rem',
        marginBottom: 10,
        lineHeight: 1.3,
      }}>
        Thanks, got it.
      </h2>
      <p style={{
        color: 'var(--ink-muted)',
        fontSize: '0.95rem',
        lineHeight: 1.6,
        maxWidth: 460,
        margin: '0 auto 20px',
      }}>
        Your suggestion is in. We won't reply to every one, but we read
        them all. If we build what you've described, we'll send a
        product-update email when it ships.
      </p>
      <button
        onClick={onSendAnother}
        className="btn btn-secondary"
        style={{ padding: '9px 20px', fontSize: '0.88rem' }}
      >
        Send another suggestion
      </button>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  fontSize: '0.95rem',
  border: '1px solid var(--surface-2)',
  borderRadius: 8,
  background: 'var(--white)',
  color: 'var(--ink)',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box',
}
